// ============================================
// OWA — content localization layer
// --------------------------------------------
// The bundled content (activityCatalog.js + mock.js) is authored in Spanish.
// This layer overlays per-language translations (from contentTranslations.js)
// on top of that base content, keyed by the item id. Any field missing a
// translation transparently falls back to the Spanish source, so the site is
// never blank while translations are still being filled in.
//
// When the API is enabled (VPS/backend), content already arrives localized via
// ?lang=, so these helpers become no-ops for API data whose ids are unknown.
// ============================================
import { useMemo } from 'react';
import { useLanguage } from './LanguageContext';
import { contentTranslations } from './contentTranslations';

// The language the bundled source content is written in.
export const BASE_CONTENT_LANG = 'es';

function overlay(base, tr) {
  if (!base || !tr) return base;
  const out = { ...base };
  for (const key of Object.keys(tr)) {
    const value = tr[key];
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  return out;
}

export function localizeActivity(activity, lang) {
  if (!activity || lang === BASE_CONTENT_LANG) return activity;
  const tr = contentTranslations.activities?.[activity.id]?.[lang];
  return overlay(activity, tr);
}

export function localizeBlogPost(post, lang) {
  if (!post || lang === BASE_CONTENT_LANG) return post;
  const tr = contentTranslations.blog?.[post.id]?.[lang];
  return overlay(post, tr);
}

export function localizeDestination(dest, lang) {
  if (!dest || lang === BASE_CONTENT_LANG) return dest;
  const key = dest.id ?? dest.slug;
  const tr = contentTranslations.destinations?.[key]?.[lang];
  return overlay(dest, tr);
}

function localizeList(list, lang, fn) {
  if (!Array.isArray(list)) return list;
  return list.map((item) => fn(item, lang));
}

// --- React hooks -------------------------------------------------------------

export function useLocalizedActivity(activity) {
  const { lang } = useLanguage();
  return useMemo(() => localizeActivity(activity, lang), [activity, lang]);
}

export function useLocalizedActivities(list) {
  const { lang } = useLanguage();
  return useMemo(() => localizeList(list, lang, localizeActivity), [list, lang]);
}

export function useLocalizedBlogPost(post) {
  const { lang } = useLanguage();
  return useMemo(() => localizeBlogPost(post, lang), [post, lang]);
}

export function useLocalizedBlogPosts(list) {
  const { lang } = useLanguage();
  return useMemo(() => localizeList(list, lang, localizeBlogPost), [list, lang]);
}

export function useLocalizedDestinations(list) {
  const { lang } = useLanguage();
  return useMemo(() => localizeList(list, lang, localizeDestination), [list, lang]);
}
